import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Pencil, Trash2, Search } from 'lucide-react';
import {
  MATERIAL_UNITS,
  MaterialItem,
  MaterialItemPayload,
  MaterialItemsApi,
} from '@/services/material-items.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Field, Input, Select, Textarea } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';
import { usePermissions } from '@/hooks/usePermissions';

const schema = z.object({
  name: z.string().min(1, 'Required'),
  description: z.string().optional(),
  unit: z.enum(MATERIAL_UNITS),
  status: z.enum(['active', 'inactive']),
});
type FormValues = z.infer<typeof schema>;

const columnHelper = createColumnHelper<MaterialItem>();

/**
 * The shared catalog — real legacy grants both Super Admin and School
 * Admin full write access (`material-item-*`); Super Admin and any Org
 * Admin always get it here too (`OrgScopedGuard` bypasses permission
 * checks for both scopes). Only a `scope="school"` caller needs an actual
 * permission check, since that's the one tier where narrower roles
 * (teacher/staff/cashier) exist without `/material-items:create`. See
 * material-items.controller.ts's doc comment.
 */
export function MaterialItemsPage({ scope }: { scope?: 'organization' | 'school' } = {}) {
  const qc = useQueryClient();
  const { can, loaded } = usePermissions(scope === 'school');
  const readOnly = scope === 'school' && loaded && !can('/material-items', 'create');
  const [modal, setModal] = useState<{ open: boolean; item?: MaterialItem }>({ open: false });
  const [confirm, setConfirm] = useState<{ open: boolean; item?: MaterialItem }>({ open: false });
  const dt = useDataTable({ defaultLimit: 20 });

  const { data, isFetching } = useQuery({
    queryKey: ['material-items', dt.params],
    queryFn: () => MaterialItemsApi.list(dt.params),
    placeholderData: keepPreviousData,
  });

  const upsert = useMutation({
    mutationFn: (vals: { id?: string; payload: MaterialItemPayload }) =>
      vals.id ? MaterialItemsApi.update(vals.id, vals.payload) : MaterialItemsApi.create(vals.payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['material-items'] });
      setModal({ open: false });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => MaterialItemsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['material-items'] });
      setConfirm({ open: false });
    },
  });

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'serial',
        header: 'S.No',
        enableSorting: false,
        cell: (info) => (dt.page - 1) * dt.limit + info.row.index + 1,
      }),
      columnHelper.accessor('name', { id: 'name', header: 'Name' }),
      columnHelper.accessor('description', {
        id: 'description',
        header: 'Description',
        cell: (info) => <span className="text-slate-500">{info.getValue() || '—'}</span>,
      }),
      columnHelper.accessor('unit', { id: 'unit', header: 'Unit', cell: (info) => info.getValue() }),
      columnHelper.accessor('status', {
        id: 'status',
        header: 'Status',
        cell: (info) => (
          <Badge tone={info.getValue() === 'active' ? 'green' : 'slate'}>{info.getValue()}</Badge>
        ),
      }),
      ...(readOnly
        ? []
        : [
            columnHelper.display({
              id: 'actions',
              header: 'Actions',
              enableSorting: false,
              cell: (info) => {
                const item = info.row.original;
                return (
                  <div className="inline-flex items-center gap-1">
                    <button
                      className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-600"
                      onClick={() => setModal({ open: true, item })}
                      title="Edit"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"
                      onClick={() => setConfirm({ open: true, item })}
                      title="Deactivate"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                );
              },
            }),
          ]),
    ],
    [readOnly, dt.page, dt.limit],
  );

  return (
    <>
      <PageHeader
        title="Material Items"
        description="The shared catalog schools request from."
        actions={
          readOnly ? undefined : (
            <button className="btn-primary" onClick={() => setModal({ open: true })}>
              <Plus className="mr-1.5 h-4 w-4" /> Add Item
            </button>
          )
        }
      />

      <DataTable
        columns={columns}
        result={data}
        state={dt}
        loading={isFetching}
        emptyMessage="No material items yet."
        toolbar={
          <div className="relative w-full sm:max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={dt.searchInput}
              onChange={(e) => dt.setSearchInput(e.target.value)}
              placeholder="Search items…"
              className="pl-9"
            />
          </div>
        }
      />

      {!readOnly && (
        <>
          <MaterialItemFormModal
            open={modal.open}
            item={modal.item}
            onClose={() => setModal({ open: false })}
            saving={upsert.isPending}
            errorMsg={errMsg(upsert.error)}
            onSubmit={(payload) =>
              upsert.mutate({
                id: modal.item?.id,
                // CreateMaterialItemDto doesn't accept `status` (new items always
                // start 'active') — the ValidationPipe's forbidNonWhitelisted
                // rejects it with "property status should not exist" if sent on
                // create. Only include it when editing.
                payload: modal.item
                  ? payload
                  : { name: payload.name, description: payload.description, unit: payload.unit },
              })
            }
          />

          <ConfirmDialog
            open={confirm.open}
            onClose={() => setConfirm({ open: false })}
            onConfirm={() => confirm.item && remove.mutate(confirm.item.id)}
            loading={remove.isPending}
            destructive={false}
            title="Deactivate item?"
            message={`"${confirm.item?.name}" will no longer be requestable by schools. Requests that already used it are unaffected.`}
            confirmText="Deactivate"
          />
        </>
      )}
    </>
  );
}

function MaterialItemFormModal({
  open,
  item,
  onClose,
  onSubmit,
  saving,
  errorMsg,
}: {
  open: boolean;
  item?: MaterialItem;
  onClose: () => void;
  onSubmit: (payload: MaterialItemPayload) => void;
  saving: boolean;
  errorMsg?: string;
}) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: {
      name: item?.name ?? '',
      description: item?.description ?? '',
      unit: item?.unit ?? 'nos',
      status: item?.status ?? 'active',
    },
  });

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title={item ? `Edit ${item.name}` : 'Add material item'}
      size="sm"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={handleSubmit(onSubmit)} disabled={saving}>
            {saving ? 'Saving…' : item ? 'Save changes' : 'Add item'}
          </button>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Field label="Name" required error={errors.name?.message}>
          <Input {...register('name')} placeholder="A4 Paper Ream" autoFocus />
        </Field>
        <Field label="Description" error={errors.description?.message}>
          <Textarea {...register('description')} rows={2} placeholder="80gsm, 500 sheets" />
        </Field>
        <Field label="Unit" required error={errors.unit?.message}>
          <Select {...register('unit')}>
            {MATERIAL_UNITS.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </Select>
        </Field>
        {item && (
          <Field label="Status" error={errors.status?.message}>
            <Select {...register('status')}>
              <option value="active">active</option>
              <option value="inactive">inactive</option>
            </Select>
          </Field>
        )}
        {errorMsg && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errorMsg}</div>}
      </form>
    </Modal>
  );
}

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}
